import { ProjectAPI } from 'api/project';
import { buildProjectOptimisticPatch } from './payload';
import { projectCollection } from './project-collection';
/**
 * Provenance for every push in this module — the WRITE layer, so each push is a
 * change this tab just made and other tabs need. Reads live in `fetchers.ts`
 * (default `'fetch'`, silent). Same convention as `collections/tasks/actions.ts`.
 */ const MUTATION_PUSH = {
    source: 'mutation'
};
/**
 * WRITE wrappers over `ProjectAPI` for CORE project mutations.
 *
 * Both update paths are **optimistic**: the collection overlay applies the edit
 * immediately and auto-rolls-back if the server errors (replacing the
 * `onMutate`/`onError`/`setQueryData` rollback dance — P12). They differ only in
 * what they return:
 * - `updateProjectOptimistic` → the `Transaction` (fire-and-forget; don't await).
 * - `updateProjectRecord` → a `Promise<Project>` resolving to the server-rehydrated
 *   project once persisted, REJECTING (after rollback) on server error — for
 *   callers that need the entity or `useMutation`/saga semantics.
 */ /**
 * Optimistic project update (the primitive). Builds an entity-shaped overlay
 * patch and carries the real `UpdateProjectPayload` through `metadata.payload`
 * so the update handler (`routeUpdate`) sends it verbatim; the server `Project`
 * re-hydrates the collection on commit, or the overlay rolls back on throw.
 *
 * Requires the project to already be in the collection — an optimistic update
 * needs the original to merge against and roll back to. If it's absent the
 * returned transaction fails fast (no API call); callers that must handle the
 * not-loaded case use `updateProjectRecord`, which falls back to a direct write.
 */ export const updateProjectOptimistic = (projectId, payload)=>{
    var _projectCollection_get;
    return projectCollection.update(projectId, buildProjectOptimisticPatch((_projectCollection_get = projectCollection.get(projectId)) !== null && _projectCollection_get !== void 0 ? _projectCollection_get : {
        projectId
    }, payload), {
        metadata: {
            payload
        }
    });
};
/**
 * Optimistic update that resolves to the server `Project` (or rejects, after the
 * overlay rolls back, on server error). The detail updates instantly via the
 * overlay; on commit the collection holds the server-confirmed value.
 *
 * If the project isn't in the collection (e.g. an RM-only project never fetched
 * into it), there's no overlay base — fall back to a direct API write + push.
 */ export const updateProjectRecord = async (projectId, payload)=>{
    if (projectCollection.get(projectId) === undefined) {
        const project = await ProjectAPI.updateProject(projectId, payload);
        projectCollection.push([
            project
        ], MUTATION_PUSH);
        return project;
    }
    const tx = updateProjectOptimistic(projectId, payload);
    await tx.isPersisted; // rejects on server error — overlay already rolled back
    // After commit the collection holds the server-rehydrated project.
    return projectCollection.get(projectId);
};
/** Create → await server `Project`, push, return it. (Not optimistic.) */ export const createProjectRecord = async (payload)=>{
    const project = await ProjectAPI.createProject(payload);
    projectCollection.push([
        project
    ], MUTATION_PUSH);
    return project;
};
/** Delete on the server, then remove from the collection WITHOUT re-deleting. */ export const deleteProjectRecord = async (projectId)=>{
    await ProjectAPI.deleteProject(projectId);
    projectCollection.externalDelete(projectId, {
        source: 'mutation'
    });
};
/** Shallow-patch a project already in the collection (no API call). */ const patchProjectInCollection = (projectId, patch)=>{
    const existing = projectCollection.get(projectId);
    if (existing) {
        projectCollection.push([
            {
                ...existing,
                ...patch
            }
        ], MUTATION_PUSH);
    }
};
/** Archive on the server, then flip the `archived` flag in the collection. */ export const archiveProjectRecord = async (projectId)=>{
    await ProjectAPI.archiveProject(projectId);
    patchProjectInCollection(projectId, {
        archived: true
    });
};
/** Unarchive on the server, then clear the `archived` flag in the collection. */ export const unarchiveProjectRecord = async (projectId)=>{
    await ProjectAPI.unarchiveProject(projectId);
    patchProjectInCollection(projectId, {
        archived: false
    });
};
/** Bulk-merge → await server `Project`, push, return it. */ export const bulkMergeProjectRecord = async (...args)=>{
    const project = await ProjectAPI.bulkMerge(...args);
    projectCollection.push([
        project
    ], MUTATION_PUSH);
    return project;
};
